import React, { useCallback } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { Route, RouteComponentProps, RouteProps } from 'react-router-dom';

import { BlockedUserPage } from './blocked-user-page';
import { UserProviderFactory } from './user-provider';

type AuthenticationRequiredPageProps = RouteProps & {
  requiresAdmin?: boolean;
  requiredHiringPermission?: string | string[];
  requiredEmployeesPermission?: string | string[];
};

/**
 * This wrapper around React-Router's Route makes ensures that the user going to route has
 * a user before rendering the render or component
 */
export const AuthenticationRequiredRoute = ({
  component,
  render,
  requiresAdmin,
  requiredHiringPermission,
  requiredEmployeesPermission,
  ...args
}: AuthenticationRequiredPageProps) => {
  const UserProvider = UserProviderFactory();
  // Lower cased props aren't recognized by Typescript as valid
  // The `render` is defined as a ReactNode. Typescript complains about the fact that it is more permissive than JSX.Element, which is just a weird limitation of Typescript.
  // Just going to cast to an any. Read more here: https://github.com/typescript-cheatsheets/react/issues/129#issuecomment-508105030
  const EnhancedRender: any = render;
  const EnhancedComponent = component;

  const WrappedEnhancedComponent = useCallback(
    (props: RouteComponentProps<any>) => (
      <UserProvider
        requiredHiringPermission={requiredHiringPermission}
        requiredEmployeesPermission={requiredEmployeesPermission}
        requiresAdmin={requiresAdmin}
      >
        {EnhancedComponent && <EnhancedComponent {...props} />}
      </UserProvider>
    ),
    [
      EnhancedComponent,
      UserProvider,
      requiredEmployeesPermission,
      requiredHiringPermission,
      requiresAdmin,
    ],
  );

  // See auth0 docs for possible error definitions https://auth0.com/docs/libraries/common-auth0-library-authentication-errors#log-in
  const { error } = useAuth0();
  // In the case of a blocked user, Auth0 redirects to us with the 'user is
  // blocked' error and no other data. The best we can detect this is by doing the below.
  // https://auth0.com/docs/users/block-and-unblock-users
  if (error?.message === 'user is blocked') {
    return <BlockedUserPage />;
  }

  return (
    <Route
      render={
        EnhancedRender
          ? (props: RouteComponentProps<any>) => (
              <UserProvider
                requiredHiringPermission={requiredHiringPermission}
                requiredEmployeesPermission={requiredEmployeesPermission}
                requiresAdmin={requiresAdmin}
              >
                <EnhancedRender {...props} />
              </UserProvider>
            )
          : undefined
      }
      component={EnhancedComponent ? WrappedEnhancedComponent : undefined}
      {...args}
    />
  );
};
